import { axiosInstance } from "@/plugins/https.ts";
import api from "@/_helpers/api.ts";
import { i18n } from "@/plugins/i18n.ts";
import { useWorkspaceStore } from "@/_store/workspace.module.ts";
import { useSnackbarStore } from "@/_store/snackbar.module.ts";
import moment from "moment/moment";
import type { EditSubscriptionData, MspWorkspaceDialogItem } from "@/_store/msp/msp.module.ts";
import { SubscriptionType } from "@/constants/workplaces.ts";
import type { DialogDataConfig } from "@/_store/dialogs.module.ts";
import type { ApplyGlobalPolicyData } from "@/_store/msp/global-policies/msp-global-policies.module.ts";

type RefreshCallback = () => Promise<unknown> | unknown;

/**
 * Calls provided callback when it exists, awaiting async functions so callers can chain updates.
 * @param callback - Optional callback to execute after a successful action.
 */
async function runRefresh(callback?: RefreshCallback) {
  if (!callback) {
    return;
  }
  await callback();
}

/**
 * Builds multipart form data for MSP workspace requests, attaching optional branding assets.
 * @param jsonData - Raw workspace payload that will be serialized to JSON.
 * @param image - Optional header icon file to upload.
 * @param emailImage - Optional email icon file to upload.
 * @returns FormData with serialized payload and provided assets.
 */
export function generateMspWorkspaceFormData(jsonData: object, image?: File, emailImage?: File) {
  const formData = new FormData();
  const data = new Blob([JSON.stringify(jsonData)], {
    type: "application/json",
  });

  if (image) {
    formData.append("headerIcon", image);
  }
  if (emailImage) {
    formData.append("emailIcon", emailImage);
  }
  formData.append("payload", data);
  return formData;
}

/**
 * Updates an MSP workspace and refreshes local state when editing the current workspace.
 * Displays a success snackbar and triggers an optional refresh callback after completion.
 * @param payload - Workspace dialog data including branding assets and subscription info.
 * @param refresh - Optional callback invoked after a successful update.
 */
export async function editMspWorkspace(payload: MspWorkspaceDialogItem, refresh?: RefreshCallback) {
  const {
    image,
    showDisabledModules,
    workspaceId,
    scanUnprotectedUsers,
    displayName,
    supportEnabled,
    subscription,
    maxProtectedUsers,
    companyName,
    branding,
    isCoroEmployeeWorkspace,
    emailImage,
    notificationsLevel,
    languageCode,
    allowPolicyOverriding,
    labels,
    mspPolicyId,
    subscriptionRules,
  } = payload;

  const jsonData = {
    companyName,
    notificationsLevel,
    maxProtectedUsers,
    displayName,
    supportEnabled,
    scanUnprotectedUsers,
    psaEnabled: true,
    branding: {
      ...branding,
    },
    subscription,
    showDisabledModules,
    isCoroEmployeeWorkspace,
    languageCode,
    allowPolicyOverriding,
    mspPolicyId,
    labels,
    subscriptionRules,
  };
  const formData = generateMspWorkspaceFormData(jsonData, image, emailImage);
  const workspaceStore = useWorkspaceStore();
  const currentWorkspace = workspaceStore.workspace.workspaceId;

  const request = {
    ...api.mspWorkspace(workspaceId),
    headers: {
      "Content-Type": "multipart/form-data",
    },
    data: formData,
    method: "PUT",
  };

  const { data } = await axiosInstance.request(request);
  if (currentWorkspace === workspaceId) {
    workspaceStore.setWorkspace({
      mfaRequired: data.mfaRequired,
      scanUnprotectedUsers: data.scanUnprotectedUsers,
      workspaceId: data.workspaceId,
      name: data.name,
      type: data.type,
      branding: data.branding,
      supportEnabled: data.supportEnabled,
      subscription,
      showDisabledModules,
      subscriptionType: data.subscriptionType,
      betaComponents: data.betaComponents ?? [],
      domain: data.domain,
      isCoronetWorkspace: data.isCoronetWorkspace,
      trialEndDate: data.trialEndDate ?? 0,
      languageCode: data.languageCode,
      allowPolicyOverriding: data.allowPolicyOverriding,
      mspPolicyShortInfo: data.mspPolicyShortInfo,
    });
  }
  await runRefresh(refresh);
  useSnackbarStore().addGenericSuccess(
    i18n.global.t("snackbar.messages.manageWorkspaces.workspaceUpdated")
  );
}

/**
 * Creates a new MSP workspace using multipart data constructed from dialog inputs.
 * Shows a success snackbar and executes the optional refresh callback after the request succeeds.
 * @param payload - Workspace dialog data including optional branding assets.
 * @param refresh - Optional callback invoked after the workspace is created.
 */
export async function createMspWorkspace(
  payload: MspWorkspaceDialogItem,
  refresh?: RefreshCallback
) {
  const { image, emailImage, ...jsonData } = payload;
  const formData = generateMspWorkspaceFormData(jsonData, image, emailImage);

  const request = {
    ...api.mspWorkspaces(),
    headers: {
      "Content-Type": "multipart/form-data",
    },
    data: formData,
    method: "POST",
  };

  await axiosInstance.request(request);
  await runRefresh(refresh);
  useSnackbarStore().addGenericSuccess(
    i18n.global.t("snackbar.messages.manageWorkspaces.workspaceCreated")
  );
}

/**
 * Converts an MSP workspace to a different type, optionally including descendant handling.
 * Runs the optional refresh callback when the conversion completes.
 * @param payload - Dialog item with workspace identifiers and conversion options.
 * @param refresh - Optional callback invoked after the workspace is converted.
 */
export async function convertMspWorkspace(
  payload: DialogDataConfig<MspWorkspaceDialogItem & { descendantConversionOption?: string }>,
  refresh?: RefreshCallback
) {
  const request = {
    ...api.convertMspWorkspace(payload.item.workspaceId),
    data: {
      type: payload.item.type,
      parentWorkspaceId: payload.item.parentWorkspaceId,
      descendantConversionOption: payload.item.descendantConversionOption,
    },
    method: "PUT",
  };
  await axiosInstance.request(request);
  await runRefresh(refresh);
}

/**
 * Restores an archived MSP workspace and triggers an optional refresh.
 * @param workspaceId - Identifier of the workspace to unarchive.
 * @param refresh - Optional callback invoked after the workspace is unarchived.
 */
export async function unarchiveMspWorkspace(workspaceId: string, refresh?: RefreshCallback) {
  const request = {
    ...api.unarchiveWorkspace(workspaceId),
  };
  await axiosInstance.request(request);
  await runRefresh(refresh);
}

/**
 * Archives an MSP workspace, shows a success notification, and runs the optional refresh callback.
 * @param workspaceId - Identifier of the workspace to archive.
 * @param refresh - Optional callback invoked after the workspace is archived.
 */
export async function archiveMspWorkspace(workspaceId: string, refresh?: RefreshCallback) {
  const request = {
    ...api.archiveWorkspace(workspaceId),
  };
  await axiosInstance.request(request);
  useSnackbarStore().addGenericSuccess(
    i18n.global.t("snackbar.messages.manageWorkspaces.workspaceArchived")
  );
  await runRefresh(refresh);
}

/**
 * Updates subscription modules and bundles for multiple workspaces.
 * Refreshes the current workspace subscription locally and shows a success snackbar on completion.
 * @param payload - Subscription update data including workspace identifiers.
 * @param refresh - Optional callback invoked after the subscriptions are updated.
 */
export async function editMspSubscription(
  payload: EditSubscriptionData,
  refresh?: RefreshCallback
) {
  const request = {
    ...api.editSubscription(),
    data: {
      workspaceIds: payload.workspaceIds,
      subscriptionDto: {
        modules: payload.modules,
        bundles: payload.bundles,
      },
    },
  };

  const { data } = await axiosInstance.request(request);
  const workspaceStore = useWorkspaceStore();

  // Only patch the store if the current workspace was affected by the update
  if (payload.workspaceIds.includes(workspaceStore.workspace.workspaceId)) {
    workspaceStore.$patch((state) => {
      state.workspace.subscription = {
        modules: payload.modules,
        bundles: payload.bundles,
      };
    });
  }

  await runRefresh(refresh);
  useSnackbarStore().addGenericSuccess(
    i18n.global.t(
      "snackbar.messages.manageWorkspaces.workspacesUpdated",
      { n: data.appliedActions },
      data.appliedActions
    )
  );
}

/**
 * Assigns labels to multiple MSP workspaces and shows a success notification.
 * @param payload - Bulk label assignment payload with label and workspace identifiers.
 * @param refresh - Optional callback invoked after the labels are updated.
 */
export async function bulkEditWorkspaceLabels(
  payload: { labelIds: string[]; workspaceIds: string[] },
  refresh?: RefreshCallback
) {
  const request = {
    ...api.bulkEditLabel(),
    method: "POST",
    data: payload,
  };
  const { data } = await axiosInstance.request(request);
  await runRefresh(refresh);
  useSnackbarStore().addGenericSuccess(
    i18n.global.t(
      "snackbar.messages.manageWorkspaces.workspacesUpdated",
      { n: data.appliedActions },
      data.appliedActions
    )
  );
}

/**
 * Changes subscription type for multiple workspaces and reloads current workspace if affected.
 * @param payload - Subscription type update payload with target workspace identifiers.
 * @param refresh - Optional callback invoked after the subscription type is updated.
 */
export async function updateMspSubscriptionType(
  payload: { workspaceIds: string[]; subscriptionType: SubscriptionType },
  refresh?: RefreshCallback
) {
  const request = {
    ...api.updateSubscriptionType(),
    data: {
      workspaceIds: payload.workspaceIds,
      subscriptionType: payload.subscriptionType,
    },
  };

  const { data } = await axiosInstance.request(request);
  const workspaceStore = useWorkspaceStore();
  if (payload.workspaceIds.includes(workspaceStore.workspace.workspaceId)) {
    await workspaceStore.getCurrentWorkspace();
  }
  await runRefresh(refresh);
  useSnackbarStore().addGenericSuccess(
    i18n.global.t(
      "snackbar.messages.manageWorkspaces.workspacesUpdated",
      { n: data.appliedActions },
      data.appliedActions
    )
  );
}

/**
 * Extends a workspace trial end date to the end of the provided day and shows a success snackbar.
 * @param payload - Dialog item with workspace identifier and desired end date.
 * @param refresh - Optional callback invoked after the trial is extended.
 */
export async function extendMspWorkspaceTrial(
  payload: DialogDataConfig<MspWorkspaceDialogItem>,
  refresh?: RefreshCallback
) {
  const request = {
    ...api.extendWorkspaceTrial(payload.item.workspaceId),
    data: {
      endDate: moment(payload.item.endDate).endOf("day").utc().valueOf(),
    },
  };
  await axiosInstance.request(request);
  await runRefresh(refresh);
  useSnackbarStore().addGenericSuccess(
    i18n.global.t("snackbar.messages.manageWorkspaces.workspaceUpdated")
  );
}

/**
 * Applies a global MSP policy to selected workspaces, skipping processing when none are provided.
 * Executes the optional refresh callback and shows a success notification after assignment.
 * @param payload - Policy identifier with target workspace identifiers.
 * @param refresh - Optional callback invoked after the policy is applied.
 */
export async function applyMspGlobalPolicy(
  payload: ApplyGlobalPolicyData,
  refresh?: RefreshCallback
) {
  if (!payload.workspaces.length) {
    return;
  }
  const request = {
    ...api.assignGlobalPolicy,
    data: {
      policyId: payload.policyId,
      workspaceIds: payload.workspaces.map((workspace) => workspace.workspaceId),
    },
  };
  await axiosInstance.request(request);
  await runRefresh(refresh);
  useSnackbarStore().addGenericSuccess(
    i18n.global.t("snackbar.messages.globalMspPolicies.policyApplied")
  );
}
